import React from 'react';
import styled from 'styled-components/macro';
import { JoinButton } from '../../buttons/JoinButton';
import { useSelector } from 'src/client/redux';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';

type Props = {
  background: string;
  logo: string;
  message?: string;
  title?: string;
};

const OutroWrapper = styled.div<{ backgroundImage: string }>`
  min-height: 1420px;
  width: 100%;
  background-image: url(${(props) => props.backgroundImage});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  @media (max-width: 1024px) {
    min-height: 432px;
  }
`;

const OutroLogo = styled.img`
  max-width: 250px;
  margin: 0 auto;
  @media (max-width: 1024px) {
    max-width: 144px;
  }
`;

const OutroTitle = styled.span`
  display: block;
  color: #fff;
  font-size: 72px;
  line-height: 1.2;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-weight: 800;
  margin: 0 auto 30px;
  text-align: center;
  @media (max-width: 1024px) {
    font-size: 52px;
    margin-bottom: 24px;
  }
  @media (max-width: 430px) {
    font-size: 32px;
    margin-bottom: 15px;
  }
`;

const OutroMessage = styled.span`
  display: block;
  color: #fff;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-weight: 700;
  font-size: 20px;
  text-align: center;
  margin-bottom: 60px;
  @media (max-width: 1024px) {
    font-size: 16px;
    margin-bottom: 24px;
  }
  @media (max-width: 430px) {
    font-size: 14px;
    margin-bottom: 32px;
  }
`;

const OutroAlign = styled.div`
  flex-basis: 100%;
  width: 100%;
  max-width: 860px;
  text-align: center;
  @media (max-width: 1024px) {
    max-width: 640px;
  }
  @media (max-width: 430px) {
    max-width: 360px;
  }
`;

const SignupLink = styled(JoinButton)`
  display: block;
  text-align: center;
  background: ${({ theme }) => theme.primaryBtn1Bg};
  color: #fff;
  font-family: Urbanist, 'Open Sans', sans-serif;
  font-weight: 700;
  font-size: 32px;
  height: 80px;
  line-height: 80px;
  width: 380px;
  border-radius: 3px;
  text-transform: uppercase;
  text-decoration: none;
  margin: 0 auto 90px;
  transition: 0.3s;

  :hover,
  :focus {
    background: ${({ theme }) => theme.primaryBtn1HoverBg};
    text-decoration: none;
    color: #fff;
  }

  @media (max-width: 1024px) {
    font-size: 18px;
    width: 220px;
    height: 60px;
    line-height: 60px;
    margin-bottom: 32px;
  }

  @media (max-width: 430px) {
    font-size: 14px;
    width: 160px;
    height: 40px;
    line-height: 40px;
  }
`;

export const Outro = (props: Props) => {
  const site = useSelector((state) => state.site);
  const showSFWSite = useShowSfwSite();

  let backgroundUrl = props.background;

  if (showSFWSite) {
    backgroundUrl = backgroundUrl.replace('_bg.jpg', '_bg_non_nude.jpg');
  }

  // allows us to create split line titles in the config (though probably not great for translations...)
  const textTokens = props.title
    ?.split(/(\n)/)
    .map((text, index) => (text === '\n' ? <br key={`br-${index}`} /> : text.trim()));

  return (
    <OutroWrapper backgroundImage={backgroundUrl}>
      <OutroAlign>
        <OutroTitle>{textTokens}</OutroTitle>
        <OutroMessage>{props.message}</OutroMessage>
        <SignupLink campaign="splash-outro">Get Access</SignupLink>
        <OutroLogo src={props.logo} alt={site.name} />
      </OutroAlign>
    </OutroWrapper>
  );
};
